import {
  Box, HStack, VStack, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Portal, Spacer, Tooltip,
} from "@chakra-ui/react";
import CDS from "design-system";
import { FiBell, FiEyeOff, FiMoreVertical, FiUser } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate, useSearchParams } from "react-router";
import { newHireJourneyPathById } from "helpers/url/pages/v1";
import { REMINDERS_JOURNEY_ID_PARAM } from "features/SendMessage/Components/SendJourneyRemindersById";
import type { DensityMode, GridJourney } from "../types";
import { dayIndicator } from "../helpers";
import { COCKPIT } from "../theme/cockpit";

const COL_PREFIX = "col:";

interface ColumnHeaderProps {
  journey: GridJourney;
  stats: { overdue: number; done: number; total: number };
  today: Date;
  density: DensityMode;
  onHide: (id: number) => void;
}

export default function ColumnHeader({ journey, stats, today, density, onHide }: ColumnHeaderProps) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  // Opens the Send Reminders modal by pushing the journey id into the URL.
  // The page-level `<SendJourneyRemindersById />` listens for this param and
  // mounts the actual modal — keeps the column header free of any stakeholder
  // data fetching.
  const handleSendReminders = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(REMINDERS_JOURNEY_ID_PARAM, String(journey.id));
        return next;
      },
      { replace: true },
    );
  };
  const sortableId = `${COL_PREFIX}${journey.id}`;
  const {
    setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging,
  } = useSortable({ id: sortableId });

  const headerStyle: React.CSSProperties = isDragging
    ? { transition }
    : { transition, transform: CSS.Transform.toString(transform) };

  const day = dayIndicator(journey.startDate, today);
  const dayMatch = day.label.match(/^(.*?)(\s·\s.*)$/);

  return (
    <Box
      ref={setNodeRef}
      position="sticky"
      top={0}
      zIndex={2}
      bg="bg.primary"
      display="flex"
      flexDirection="column"
      borderRight="1px solid"
      borderRightColor="gray.100"
      borderBottom="1px solid"
      borderBottomColor="gray.100"
      opacity={isDragging ? 0.3 : 1}
      style={headerStyle}
    >
      {/* Slot is always rendered (transparent when not overdue) so every
          column header stays the same total height — without this, the red
          banner pushes the name/day block down on overdue columns and breaks
          vertical alignment with non-overdue ones. */}
      <Box
        bg={stats.overdue > 0 ? COCKPIT.red.fill : "transparent"}
        color={COCKPIT.red.mark}
        fontSize="10px"
        fontWeight="semibold"
        textAlign="center"
        py={0.5}
        minH="16px"
        letterSpacing="wide"
      >
        {stats.overdue > 0 && `${stats.overdue} overdue`}
      </Box>

      <Box display="flex" alignItems="stretch" minH="44px" flex={1}>
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          color="gray.400"
          cursor="grab"
          _active={{ cursor: "grabbing" }}
          _hover={{ color: "gray.600" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minW="18px"
          flexShrink={0}
          style={{ touchAction: "none" }}
        >
          <Box as={MdDragIndicator} boxSize={3.5} aria-label="Drag to reorder column" />
        </Box>

        <Box flex={1} minW={0} bg="bg.primary" pl={1.5} pr={1.5} py={1}>
        <HStack spacing={1} align="center" w="100%">
          <VStack align="start" spacing={0.5} flex={1} minW={0}>
            <HStack w="100%" justify="space-between" align="center" spacing={1}>
              <Tooltip label={journey.newHire?.fullName ?? "Unknown"} placement="top" openDelay={400} hasArrow>
                <Box minW={0} overflow="hidden">
                  <CDS.Text12
                    fontWeight="semibold"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    cursor="default"
                  >
                    {density === "compact"
                      ? (journey.newHire?.firstName ?? journey.newHire?.fullName ?? "Unknown")
                      : (journey.newHire?.fullName ?? "Unknown")}
                  </CDS.Text12>
                </Box>
              </Tooltip>
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  aria-label="New hire actions"
                  icon={<FiMoreVertical size={12} />}
                  size="xs"
                  variant="ghost"
                  minW="14px"
                  w="14px"
                  h="16px"
                  px={0}
                  flexShrink={0}
                />
                <Portal>
                  <MenuList minW="220px">
                    <MenuItem icon={<FiUser size={14} />} onClick={() => navigate(newHireJourneyPathById(String(journey.id)))}>
                      View journey
                    </MenuItem>
                    <MenuItem icon={<FiBell size={14} />} onClick={handleSendReminders}>
                      <HStack w="100%" spacing={2}>
                        <Box>Send reminders</Box>
                        <Spacer />
                        {stats.overdue > 0 && (
                          <CDS.Text10 color="gray.500" whiteSpace="nowrap">
                            {stats.overdue}
                            {" due"}
                          </CDS.Text10>
                        )}
                      </HStack>
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem icon={<FiEyeOff size={14} />} color="red.600" onClick={() => onHide(journey.id)}>
                      Hide from view
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </HStack>
            <Tooltip label={day.label} placement="top" hasArrow openDelay={300}>
              <Box minW={0} overflow="hidden" cursor="default">
                <CDS.Text12
                  color="gray.500"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {dayMatch ? dayMatch[1] : day.label}
                </CDS.Text12>
              </Box>
            </Tooltip>
          </VStack>
        </HStack>
        </Box>
      </Box>
    </Box>
  );
}
